/**
 * React component representing the hero section.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.data - Data for the component, including information for the hero section.
 * @param {Function} props.setHeroSectionInView - Function to set whether the hero section is in view.
 * @param {boolean} props.isUberBannerPresent - Flag indicating whether the Uber banner is present.
 * @param {Object} props.pageLoadAnimationData - Data for the page load animation.
 * @param {Object} props.commonAssets - Common assets for the component.
 * @returns {JSX.Element} React component.
 */

import React, { useEffect, useState, useRef } from "react";
import HeroDescription from "./HeroDescription";
import styles from "./heroSection.module.scss";
import VideoModal from "../VideoModal";
import VideoTile from "./VideoTile";
import WiredReviewCard from "../Wired/Wired";
import GizmodoReviewCard from "../Wired/Gizmodo";
import PageLoadAnimation from "../PageLoadAnimation";
import { motion, useAnimation, useInView } from "framer-motion";
import Image from "next/image";
import LazyVideoNew from "../LazyVideo/LazyVideoNew";
import LazyVideo from "../LazyVideo/LazyVideo";
import mobileHero from "@assets/images/homepage/hero-mobile.webp";
import desktopHero from "@assets/images/homepage/hero-desktop.webp";

export default function HeroSection({
  data,
  setHeroSectionInView,
  isUberBannerPresent,
  pageLoadAnimationData,
  commonAssets,
  maticProduct,
}) {
  const [isOpen, SetIsOpen] = useState(false);
  const ref = useRef();
  const inView = useInView(ref);

  const animationControls = useAnimation();
  const [shouldStartAnimation, setStartAnimation] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isHeroVideoLoaded, setIsHeroVideoLoaded] = useState(true);
  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);

  const toggleVideoModal = () => {
    SetIsOpen(!isOpen);
  };

  useEffect(() => {
    const isShowBannerKeyPresent = sessionStorage.getItem("showUberBanner");
    if (isShowBannerKeyPresent !== "false") {
      setIsBannerVisible(true);
    }
  }, []);

  useEffect(() => {
    animationControls.start("visible");
  }, []);

  // useEffect(() => {
  //   if (shouldStartAnimation) {
  //     animationControls.start("visible");
  //   }
  // }, [shouldStartAnimation]);

  useEffect(() => {
    setHeroSectionInView(inView);
  }, [inView]);

  return (
    <>
      <section
        ref={ref}
        className={`heroSection hidden lg:block relative w-screen h-[90svh] ${styles.heroContainer}`}
        id="homeHeroSection"
      >
        {/* <LazyVideoNew
          videoImagePath={data.heroSectionBackgroundVideoContainer}
          isMobile={false}
          customClasses={`h-full w-full object-cover object-center`}
          setIsHeroVideoLoaded={setIsHeroVideoLoaded}
          shouldPause={isAnyModalOpen}
        /> */}
        <LazyVideo
          videoSrc="/videos/Matic_Demo_GENERIC.mp4"
          image={desktopHero}
          customClasses={`h-full w-full object-cover object-center`}
          setIsHeroVideoLoaded={setIsHeroVideoLoaded}
          shouldPause={isAnyModalOpen}
          priority={true}
        />
       
        <motion.div
          className="absolute w-full z-[1]"
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1 },
          }}
          initial="hidden"
          animate={animationControls}
          transition={{
            duration: 0.5,
            ease: [0, 0.5, 0.71, 0.2, 1.01],
          }}
        >
          {/* <div className="flex flex-col md:justify-between md:flex-row bottom-24 md:bottom-10 px-[24px] xl:px-[80px] absolute w-full items-end">
            <HeroDescription data={data} commonAssets={commonAssets} />
            <VideoTile data={data} openModal={toggleVideoModal} />
          </div> */}
          
          <div className={`${styles.infoAndVideoContainer} gridLayout bottom-24 md:bottom-10 absolute items-end`}>
            <div className="col-span-7"> <HeroDescription data={data} commonAssets={commonAssets} maticProduct={maticProduct} /></div>
            <div className="col-span-5 justify-self-end flex-col hidden lg:flex max-w-[420px]">
              <WiredReviewCard />
              <GizmodoReviewCard />
              <div className="self-end">
                <VideoTile data={data} openModal={toggleVideoModal} setIsAnyModalOpen={setIsAnyModalOpen} className="self-end" />
              </div>
            </div>
          </div>
        </motion.div>
          <VideoModal isOpen={isOpen} toggleVideoModal={toggleVideoModal} />
            
      </section>

      {/* Moblie view */}
      <section
        ref={ref}
        className={`heroSection block lg:hidden relative w-screen h-[90svh] ${styles.heroContainer}`}
        id="homeHeroSection"
      >
        <LazyVideo
          videoSrc="/videos/Home_Hero_Generic_Vertical.mp4"
          image={mobileHero}
          customClasses={`h-full w-full object-cover object-center`}
          setIsHeroVideoLoaded={setIsHeroVideoLoaded}
          shouldPause={isAnyModalOpen}
          priority={true}
        />
        <motion.div
          className="absolute w-full z-[1]"
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1 },
          }}
          initial="hidden"
          animate={animationControls}
          transition={{
            duration: 0.5,
            ease: [0, 0.5, 0.71, 0.2, 1.01],
          }}
        >
          <div className="flex flex-col md:justify-between md:flex-row bottom-24 md:bottom-10 px-[24px] xl:px-[80px] absolute w-full items-end">
            <HeroDescription data={data} commonAssets={commonAssets} maticProduct={maticProduct} />
            <VideoTile data={data} openModal={toggleVideoModal} setIsAnyModalOpen={setIsAnyModalOpen} />
          </div>
        </motion.div>
        <VideoModal isOpen={isOpen} toggleVideoModal={toggleVideoModal} />
      </section>
      {/* <section className="absolute top-0 w-full h-screen">
        <PageLoadAnimation
          data={pageLoadAnimationData}
          setTextAnimation={setStartAnimation}
          isHeroVideoLoaded={isHeroVideoLoaded}
        />
      </section> */}
      <div className="mt-[40px] mx-6 lg:mx-[80px] lg:hidden"> 
        <WiredReviewCard />
        <GizmodoReviewCard />
      </div>
    </>
  );
}
